import React, { useState, useEffect, useMemo } from 'react';
import ManagementList from '../components/ManagementList';
import { fetchDevices, deleteDevice } from '../services/api'; 
import { LIMIT } from '../lib/utils/constant';
import { usePermissions } from '../Hooks/usePermission';
import { PERMISSIONS } from '../lib/utils/permissions';
import PermissionMessage from '../components/ErrorMessage/PermissionMessage';

function DeviceManagement() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  // State for pagination and search
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(LIMIT[0].value)
  const [counts, setCounts] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { can } = usePermissions();
  const userRoleCode = JSON.parse(localStorage.getItem('user'))?.role?.code;
  const canViewDevice = can(PERMISSIONS.DEVICE_READ, userRoleCode);
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Filter State
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'Assigned', 'Unassigned'

  // Date Range State
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  const [dateRange, setDateRange] = useState({
    start: thirtyDaysAgo.toISOString(),
    end: today.toISOString()
  });

  // --- 1. Define Columns ---
  const columns = [
    { header: 'DEVICE ID', accessor: 'deviceId', sortable: true, sortKey: 'id' },
    { header: 'DEVICE IMEI', accessor: 'imei', sortable: true, sortKey: 'imei' },
    { header: 'MODEL', accessor: 'model', sortable: true, sortKey: 'model' },
    // Flattened SIM details
    { header: 'SIM ICCID', accessor: 'simIccid', sortable: true, sortKey: 'currentSim.iccid' },
    { header: 'SIM NUMBER', accessor: 'simNumber', sortable: true, sortKey: 'currentSim.msisdn' },
    { header: 'SIM CARRIER', accessor: 'simCarrier', sortable: true, sortKey: 'currentSim.carrier' },
  ];

   const handleLimitChange = (e) => {
    setItemsPerPage(e.target.value,'val')
   }

  // --- 2. Load Data from API ---
  const loadDevices = async () => {
    setLoading(true);
    try {
      // Determine isAssigned based on tab
      let isAssigned = true;
      if (statusFilter === 'assigned') isAssigned = true;
      else if (statusFilter === 'unassigned') isAssigned = false;

      const payload = {
        start: null,
        end: null,
        isAssigned: isAssigned,
        page: page,
        limit: itemsPerPage,
        // search: searchTerm, // Uncomment if API supports search in this payload
        // sortBy: sortConfig.key,
        // sortOrder: sortConfig.direction
      };

      console.log("Fetching Devices with payload:", payload);
      
      const response = await fetchDevices(payload); 
      
      if (response.data && response.data.success) {
        // Handle different response structures (paged vs list)
        let items = [];
        
        if (response.data.data && Array.isArray(response.data.data.items)) {
             items = response.data.data.items;
             // handle server pagination meta if available
             if (response.data.data.pagination) {
               setTotalPages(response.data.data.pagination.pages || 1);
             }
             if (response.data.data.counts) {
               setCounts(response.data.data.counts);
             }
        } else if (Array.isArray(response.data.data)) {
             items = response.data.data;
        }

        const mappedDevices = items.map(d => ({
          id: d.id,
          deviceId: d.id, // Display ID
          imei: d.imei || 'N/A',
          model: d.model || 'N/A',
          // Access nested SIM details safely
          simIccid: d.currentSim?.iccid || '-',
          simNumber: d.currentSim?.msisdn || '-',
          simCarrier: d.currentSim?.carrier || '-',
          
          status: d.isAssigned ? 'Assigned' : 'Unassigned',
          
          // Store raw for sorting/filtering
          originalData: d
        }));

        setDevices(mappedDevices);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error("Error loading devices:", error);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [page, statusFilter, dateRange, itemsPerPage]); // Removed searchTerm/sortConfig to strictly follow payload req, or add back if API supports

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this device?")) {
      try {
        await deleteDevice(id);
        loadDevices();
      } catch (err) {
        console.error("Error deleting device:", err);
      }
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    // setPage(1); // If searching client side
  };
  
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  const handlePageChange = (newPage) => {
      setPage(newPage);
  };

  const handleTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  }
  
  const handleClearFilter = () => {
    setDateRange({
      start: thirtyDaysAgo.toISOString(),
      end: today.toISOString()
    });
    setSearchTerm('');
    setPage(1);
  };


  // Client-side filtering/sorting (fallback or additional)
  const filteredDevices = useMemo(() => {
      let data = [...devices];
      
      if (searchTerm) {
          const lowerTerm = searchTerm.toLowerCase();
          data = data.filter(d => 
              (d.imei && d.imei.toLowerCase().includes(lowerTerm)) ||
              (d.model && d.model.toLowerCase().includes(lowerTerm)) ||
              (d.simIccid && d.simIccid.toLowerCase().includes(lowerTerm))
          );
      }
      
      if (sortConfig.key) {
          data.sort((a, b) => {
              let valA = a[sortConfig.key] || '';
              let valB = b[sortConfig.key] || '';
              if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
              if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
              return 0;
          });
      }
      
      return data;
  }, [devices, searchTerm, sortConfig]);

  return (
    <>
      {canViewDevice ? <ManagementList 
        title="Devices" 
        data={filteredDevices} 
        columns={columns} 
        // addNewLink="/devices/new" // Uncomment if you have an add page
        onDelete={handleDelete}
        
        // Switched to Server Side logic
        isServerSide={true} 
        isLoading={loading}
        serverPage={page}
        serverTotalPages={totalPages}
        serverCounts={counts}
        onPageChange={handlePageChange}
        onTabChange={handleTabChange}
        
        onSearch={handleSearch}
        //onSort={handleSort}
        //sortConfig={sortConfig}
        
        // Custom tabs for Assigned/Unassigned
        customTabs={[
            { eventKey: 'assigned', title: 'Assigned' },
            { eventKey: 'unassigned', title: 'Unassigned' }
        ]}
        limit={itemsPerPage}
        handleLimitChange={handleLimitChange}
      /> : <PermissionMessage  />}
    </>
  );
}

export default DeviceManagement;